<template>
  <div class="flex h-screen bg-[#f3f4f8]">
    <!-- Sidebar Component -->
    <DelivererSidebar :isOpen="sidebarOpen" @close="sidebarOpen = false" />
    <!-- Overlay for mobile when sidebar open -->
    <div v-if="sidebarOpen" class="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden" @click="sidebarOpen = false">
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto relative z-10 transition-all duration-300">
      <!-- Header with Back Button -->
      <header class="bg-white shadow-sm sticky top-0 z-30">
        <div class="px-6 py-4 flex items-center justify-between">
          <!-- Left: Burger + Text -->
          <div class="flex items-center space-x-4">
            <!-- Sidebar toggle button (burger) -->
            <button @click="$router.back()"
              class="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200">
              <i class="fas fa-arrow-left text-gray-600"></i>
            </button>

            <!-- Header Text -->
            <div class="flex flex-col">
              <span class="text-[#4d148c] font-medium text-sm md:text-base">Item Details</span>
              <h2 class="text-gray-500 text-sm mt-0.5">Review item information and request delivery</h2>
            </div>
          </div>

          <!-- Optional right side (if you want user/profile) -->
          <div class="hidden md:flex items-center space-x-4">
            <!-- Example: User avatar or actions -->
          </div>
        </div>
      </header>

      <!-- Error Popup -->
      <div v-if="showErrorPopup"
        class="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg shadow-lg z-50 animate-slideInDown">
        <div class="flex items-center">
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ errorMessage }}
          <button @click="showErrorPopup = false" class="ml-4 text-red-400 hover:text-red-600 focus:outline-none">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center h-[calc(100vh-100px)]">
        <div class="text-center">
          <i class="fas fa-circle-notch fa-spin text-3xl text-[#4d148c]"></i>
          <p class="mt-4 text-gray-600">Loading item details...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="container mx-auto px-4 py-6">
        <div class="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {{ error }}
        </div>
      </div>

      <!-- Item Details -->
      <div v-else-if="item" class="container mx-auto px-4 py-6">
        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
          <!-- Status Banner -->

          <div class="px-6 py-3 text-sm font-medium" :class="getStatusClass(item.status)">
            Status: {{ item.status?.charAt(0).toUpperCase() + item.status?.slice(1) }}
          </div>

          <!-- Main Content -->
          <div class="p-6 space-y-6">
            <!-- Basic Info -->
            <div class="flex justify-between items-start">
              <div>
                <h1 class="text-2xl font-bold text-gray-900">{{ item.name }}</h1>
                <!-- <p class="mt-1 text-sm text-gray-500">ID: #{{ item.id }}</p> -->
              </div>
              <div class="flex items-center space-x-2">
                <span v-if="item.is_fragile"
                  class="px-3 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-full">
                  Fragile
                </span>
              </div>
            </div>

            <!-- Pricing Info -->
            <div v-if="showCardForm"
              class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div
                class="bg-gradient-to-br from-purple-700 to-purple-900 text-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-slideInDown relative">
                <h3 class="text-2xl font-semibold mb-6 tracking-wide">Enter Payment Details</h3>

                <!-- Card Number -->
                <label for="card-number-element"
                  class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-300">
                  Card Number
                </label>
                <div id="card-number-element"
                  class="bg-purple-800 rounded-lg p-3 mb-6 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                </div>

                <div class="flex space-x-4 mb-6">
                  <!-- Expiry Date -->
                  <div class="flex-1">
                    <label for="card-expiry-element"
                      class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-300">
                      Expiry Date
                    </label>
                    <div id="card-expiry-element"
                      class="bg-purple-800 rounded-lg p-3 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                    </div>
                  </div>

                  <!-- CVC -->
                  <div class="flex-1">
                    <label for="card-cvc-element"
                      class="block mb-2 text-sm font-semibold uppercase tracking-widest text-purple-300">
                      CVC
                    </label>
                    <div id="card-cvc-element"
                      class="bg-purple-800 rounded-lg p-3 focus-within:ring-2 focus-within:ring-purple-400 transition-shadow cursor-text">
                    </div>
                  </div>
                </div>

                <!-- Error Message -->
                <p v-if="cardError" class="text-red-400 text-sm mb-4 font-medium">{{ cardError }}</p>

                <!-- Buttons -->
                <div class="flex justify-end space-x-3">
                  <button @click="cancelCardPayment"
                    class="inline-flex items-center justify-center px-5 py-2 border-2 border-purple-300 rounded-lg font-semibold text-purple-300 hover:bg-purple-300 hover:text-purple-900 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2">
                    Cancel
                  </button>

                  <button @click="confirmCardPayment" :disabled="confirmingPayment"
                    class="inline-flex items-center justify-center px-5 py-2 bg-purple-600 rounded-lg font-semibold text-white hover:bg-purple-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2">
                    <template v-if="confirmingPayment">
                      <i class="fas fa-circle-notch fa-spin mr-2"></i> Processing...
                    </template>
                    <template v-else>
                      Payment Confirm
                    </template>
                  </button>
                </div>

                <!-- Optional: Small decorative chip or logo to enhance card look -->
                <div class="absolute top-6 right-6 w-12 h-8 bg-yellow-400 rounded-md shadow-md" title="Card Chip"></div>
              </div>
            </div>



            <!-- Description -->
            <div class="bg-gray-100 rounded-lg p-4">
              <h3 class="text-sm font-medium text-gray-700 mb-2">Description</h3>
              <p class="text-gray-600">{{ item.description || 'No description provided' }}</p>
            </div>

            <!-- Details Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Locations -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-700">Delivery Route</h3>
                <div class="flex items-start space-x-3">
                  <i class="fas fa-map-marker-alt w-5 text-[#4d148c] mt-1"></i>
                  <div>
                    <div class="text-sm text-gray-600">
                      <span class="font-medium">From:</span> {{ item.from_location }}
                    </div>
                    <div class="text-sm text-gray-600 mt-1">
                      <span class="font-medium">To:</span> {{ item.to_location }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deadline -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-700">Delivery Deadline</h3>
                <div class="flex items-start space-x-3">
                  <i class="fas fa-calendar w-5 text-[#4d148c] mt-1"></i>
                  <div class="text-sm text-gray-600">
                    {{ formatDate(item.delivery_deadline) }}
                  </div>
                </div>
              </div>

              <!-- Package Details -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-700">Package Details</h3>
                <div class="space-y-2">
                  <div class="flex items-center space-x-3">
                    <i class="fas fa-box w-5 text-[#4d148c]"></i>
                    <span class="text-sm text-gray-600">
                      Quantity: {{ item.quantity }}
                    </span>
                  </div>
                  <div class="flex items-center space-x-3">
                    <i class="fas fa-weight-hanging w-5 text-[#4d148c]"></i>
                    <span class="text-sm text-gray-600">
                      Weight: {{ item.weight }}kg
                    </span>
                  </div>
                  <div class="flex items-center space-x-3">
                    <i class="fas fa-ruler-combined w-5 text-[#4d148c]"></i>
                    <span class="text-sm text-gray-600">
                      Dimensions: {{ item.dimensions?.length }}x{{ item.dimensions?.width }}x{{ item.dimensions?.height
                      }} cm
                    </span>
                  </div>
                </div>
              </div>

              <!-- Category -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-700">Category</h3>
                <div class="flex items-start space-x-3">
                  <i class="fas fa-tag w-5 text-[#4d148c] mt-1"></i>
                  <div class="text-sm text-gray-600 capitalize">
                    {{ item.category }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Special Instructions -->
            <div v-if="item.special_instructions" class="bg-blue-50 rounded-lg p-4">
              <h3 class="text-sm font-medium text-blue-700 mb-2">Special Instructions</h3>
              <p class="text-sm text-blue-600">{{ item.special_instructions }}</p>
            </div>

            <!-- Request Button Section -->
            <div class="mt-8 space-y-4">
              <!-- Proof of Travel Upload -->
              <div v-if="!isItemRequested" class="bg-gray-50 rounded-lg p-4">
                <h3 class="text-sm font-medium text-gray-700 mb-2">Proof of Travel Document</h3>
                <p class="text-sm text-gray-500 mb-3">
                  Please upload a document proving your travel plans (e.g., ticket, booking confirmation)
                </p>
                <div class="flex items-center space-x-2">
                  <label
                    class="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <input type="file" class="hidden" accept=".pdf,.jpg,.jpeg,.png" @change="handleFileUpload">
                    <i class="fas fa-upload mr-2"></i>
                    Choose File
                  </label>
                  <span class="text-sm text-gray-500">
                    {{ selectedFile ? selectedFile.name : 'No file chosen' }}
                  </span>
                </div>
                <p class="mt-2 text-xs text-gray-500">
                  Accepted formats: PDF, JPEG, PNG. Maximum size: 2MB
                </p>
                <div v-if="fileError" class="mt-2 text-sm text-red-600">
                  {{ fileError }}
                </div>
              </div>

              <!-- Request Button -->
              <button @click="showConsent = true"
                class="w-full md:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#4d148c] hover:bg-[#6d2ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4d148c] disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isItemRequested || !selectedFile">
                <i class="fas fa-hand-holding-box mr-2"></i>
                {{ getRequestButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConsentDialog :visible="showConsent" :role="currentRole" :terms="filteredTermsDialog"
        @accept="handleConsentAccepted" @cancel="showConsent = false" />
    </div>
  </div>
</template>

<script>
import DelivererSidebar from '@/components/DelivererSidebar.vue';
import api from '../services/api';
import ConsentDialog from '@/components/ConsentDialog.vue';
import { useToast } from 'vue-toastification';
import router from '@/router';
export default {
  components: {
    DelivererSidebar,
    ConsentDialog
  },
  name: 'ItemDetail',
  data() {
    return {
      item: null,
      loading: false,
      error: null,
      navigation: [
        { name: 'Dashboard', path: '/deliverer-dashboard', icon: 'fas fa-chart-line' },
        { name: 'Available Items', path: '/available-items', icon: 'fas fa-box-open' },
        { name: 'My Deliveries', path: '/my-deliveries', icon: 'fas fa-shipping-fast' },
        { name: 'Rejected Requests', path: '/rejected-requests', icon: 'fas fa-times' }
      ],
      userName: '',
      userInitials: '',
      showErrorPopup: false,
      errorMessage: '',
      errorPopupTimeout: null,
      selectedFile: null,
      fileError: null,
      //Dilaog
      showConsent: false,
      currentRole: 'deliverer',
      terms: [],
      deliverer_offer_price: null,
      showOfferInput: false,
      sidebarOpen: false

    }
  },
  computed: {
    filteredTermsDialog() {
      return this.terms.filter(t => t.role === this.currentRole);
    },
    isItemRequested() {
      if (!this.item || !this.item.requested_by) return false;
      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      return this.item.requested_by.includes(userId) || this.item.request_status === 'accepted';
    },
    getRequestButtonText() {
      if (!this.item) return '';
      if (this.isItemRequested) {
        return 'Requested';
      }
      if (!this.selectedFile) {
        return 'Upload Travel Document to Request';
      }
      return 'Request to Deliver';
    },

  },
  created() {
    this.setUserInfo();
    this.loadItem();
    this.fetchTermsAndConditions();
  },
  methods: {
    fetchTermsAndConditions() {
      const toast = useToast();
      api.get('api/user-consent')
        .then(response => {
          this.terms = response.data.data;
        })
        .catch(error => {
          console.error("Error fetching terms:", error);
          toast.error("Failed to fetch terms.");
        });
    },
    handleConsentAccepted() {
      this.showConsent = false;
      this.requestDelivery();
    },
    setUserInfo() {
      const user = JSON.parse(localStorage.getItem('user'));
      this.userName = user?.name || 'Demo User';
      this.userInitials = user?.name
        ? user.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
        : 'DU';
    },
    async loadItem() {
      try {
        this.loading = true;
        this.error = null;
        const response = await api.get(`/api/item/${this.$route.params.id}`);

        if (response.data.success) {
          this.item = response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to load item details');
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to load item details';
        console.error('Error loading item:', error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getStatusClass(status) {
      const baseClasses = 'bg-opacity-10 border-b';
      const classes = {
        pending: `bg-yellow-500 ${baseClasses} text-yellow-700`,
        accepted: `bg-green-500 ${baseClasses} text-green-700`,
        rejected: `bg-red-500 ${baseClasses} text-red-700`,
        completed: `bg-blue-500 ${baseClasses} text-blue-700`,
      };
      return classes[status] || `bg-gray-500 ${baseClasses} text-gray-700`;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.fileError = null;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.fileError = 'Invalid file type. Please upload a PDF, JPEG, or PNG file.';
        this.selectedFile = null;
        return;
      }

      // Validate file size (2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        this.fileError = 'File size must be less than 2MB';
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    },
    async requestDelivery() {
      let toast = useToast()
      if (!this.selectedFile) {
        this.fileError = 'Please upload a proof of travel document';
        return;
      }

      try {
        this.loading = true;

        // Create form data
        const formData = new FormData();
        formData.append('item_id', this.$route.params.id);
        formData.append('proof_of_travel', this.selectedFile);
        formData.append(' accept_privacy', true,);
        if (this.deliverer_offer_price !== null && this.deliverer_offer_price >= 0) {
          formData.append('deliverer_price', this.deliverer_offer_price);
        } else {
          formData.append('deliverer_price', this.item.sender_price); // Default to sender's price if not set
        }

        const response = await api.post('/api/request-item', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          toast.success('Request sent successfully!');
          // Reload the item details to get fresh data
          router.push('/available-items');
          await this.loadItem();
        } else {
          throw new Error(response.data.message || 'Failed to request item');
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || 'Failed to request item';
        this.showErrorPopup = true;

        if (this.errorPopupTimeout) {
          clearTimeout(this.errorPopupTimeout);
        }

        this.errorPopupTimeout = setTimeout(() => {
          this.showErrorPopup = false;
          this.errorMessage = '';
        }, 3000);

        console.error('Error requesting item:', error);
      } finally {
        this.loading = false;
      }
    },
    validatePositiveNumber(event, field) {
      const value = event.target.value;

      // Allow clearing
      if (value === '' || value === null) {
        this[field] = null;
        return;
      }

      const number = parseFloat(value);

      if (isNaN(number) || number < 0) {
        this[field] = null;
        return;
      }

      // Ensure 2 decimal places max
      this[field] = Math.floor(number * 100) / 100;
    }
    ,

    allowOnlyMoneyInput(event) {
      const key = event.key;

      // Allow control keys
      if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) {
        return;
      }

      // Only allow digits and one dot
      const currentValue = event.target.value;
      const futureValue = currentValue + key;

      // Matches only positive numbers with max 2 decimal places
      const regex = /^\d*\.?\d{0,2}$/;

      if (!regex.test(futureValue)) {
        event.preventDefault();
      }
    }


  },
  beforeUnmount() {
    // Clear timeout if component is unmounted
    if (this.errorPopupTimeout) {
      clearTimeout(this.errorPopupTimeout);
    }
  }
}
</script>

<style scoped>
/* Reuse animations from AvailableItems.vue */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-slideInLeft {
  animation: slideInLeft 0.6s ease-out forwards;
}

.animate-slideInDown {
  animation: slideInDown 0.6s ease-out forwards;
}

.animate-fadeIn {
  animation: fadeIn 0.6s ease-out forwards;
}
</style>